import type { UserRole } from './users';

export type WelcomeTourRole = UserRole | 'dancer';

export interface WelcomeTourStep {
  id: string;
  title: string;
  body: string;
  href?: string;
  actionLabel?: string;
}

export const WELCOME_TOUR_EVENT = 'ldg:welcome-tour-open';
const WELCOME_TOUR_STORAGE_VERSION = 'v1';

export function welcomeTourStorageKey(viewerKey: string): string {
  return `ldgWelcomeTour:${WELCOME_TOUR_STORAGE_VERSION}:${viewerKey}`;
}

export function hasFinishedWelcomeTour(viewerKey: string): boolean {
  try {
    return window.localStorage.getItem(welcomeTourStorageKey(viewerKey)) === 'done';
  } catch {
    return false;
  }
}

/**
 * Fired when a viewer finishes or skips the tour, with her viewerKey as the
 * detail, so a pop-up waiting its turn (the dancer's turn-on-notifications
 * pop-up, pages/landing/dancer/DancerPushPopup.tsx) can show after it rather
 * than on top of it, even where the "done" record cannot be saved.
 */
export const WELCOME_TOUR_DONE_EVENT = 'ldg:welcome-tour-done';

export function finishWelcomeTour(viewerKey: string): void {
  try {
    window.localStorage.setItem(welcomeTourStorageKey(viewerKey), 'done');
  } catch {
    // Private browsing and a full device can disable localStorage. Finishing
    // still closes the tour for this visit; the next sign-in may offer it again.
  }
  try {
    window.dispatchEvent(new CustomEvent(WELCOME_TOUR_DONE_EVENT, { detail: viewerKey }));
  } catch {
    // No window: nothing is waiting.
  }
}

export function openWelcomeTour(): void {
  window.dispatchEvent(new Event(WELCOME_TOUR_EVENT));
}

const WELCOME_STEP: WelcomeTourStep = {
  id: 'welcome',
  title: 'Welcome to the LDG Hub',
  body: 'Here is a quick look at the places you will use most. You can skip now and reopen this tour from the menu any time.',
};

const STAFF_STEPS: readonly WelcomeTourStep[] = [
  WELCOME_STEP,
  {
    id: 'messages',
    title: 'Messages keep conversations together',
    body: 'Send a direct message, message a class, or start your own group. Pin important conversations so they stay easy to find.',
    href: '/messages',
    actionLabel: 'Open Messages',
  },
  {
    id: 'polls',
    title: 'To-dos can collect answers',
    body: 'Create an availability poll when you need people to pick the times that work. To-dos also collect music, files, payments, and other follow-ups.',
    href: '/tasks',
    actionLabel: 'Open To-dos and polls',
  },
  {
    id: 'music',
    title: 'Music and videos live with the class',
    body: 'Open My classes, choose a class, and keep its music, videos, updates, and roster together in one place.',
    href: '/teach/classes',
    actionLabel: 'Open My classes',
  },
  {
    id: 'calendar',
    title: 'The Calendar stays current',
    body: 'See classes, rehearsals, closures, and room plans. You can also link the live schedule to your phone calendar.',
    href: '/calendar',
    actionLabel: 'Open Calendar',
  },
];

const DIRECTOR_EXTRA_STEP: WelcomeTourStep = {
  id: 'command-center',
  title: 'Command Center sees the whole studio',
  body: 'Search every class update when you need it without putting every class conversation in your personal Messages inbox.',
  href: '/command-center',
  actionLabel: 'Open Command Center',
};

const FRONT_DESK_STEPS: readonly WelcomeTourStep[] = [
  ...STAFF_STEPS.filter((step) => step.id !== 'music'),
  {
    id: 'classes',
    title: 'Class information is in Classes',
    body: 'Use Classes to look up schedules, rosters, and studio details when you are helping families at the front desk.',
    href: '/offerings',
    actionLabel: 'Open Classes',
  },
];

const FAMILY_STEPS: readonly WelcomeTourStep[] = [
  WELCOME_STEP,
  {
    id: 'messages',
    title: 'Messages and to-dos stay together',
    body: 'Read studio messages, reply to conversations, share photos, and see anything that needs your attention.',
    href: '/messages',
    actionLabel: 'Open Messages',
  },
  {
    id: 'polls',
    title: 'Polls and requests appear in To-dos',
    body: 'Share the times that work, upload requested music or files, and mark each item done when you finish it.',
    href: '/tasks',
    actionLabel: 'Open To-dos',
  },
  {
    id: 'music',
    title: 'Find your dancer\'s music and videos',
    body: 'Practice links and approved class media are organized by dancer and class so you do not have to hunt through old messages.',
    href: '/family/videos',
    actionLabel: 'Open Music and videos',
  },
  {
    id: 'calendar',
    title: 'Your family Calendar is personal',
    body: 'See the schedule for your linked dancers and add the live Hub calendar to your phone.',
    href: '/family/calendar',
    actionLabel: 'Open My calendar',
  },
];

const DANCER_STEPS: readonly WelcomeTourStep[] = [
  WELCOME_STEP,
  {
    id: 'todos',
    title: 'My to-dos shows what is waiting',
    body: 'Check off your own studio to-dos and ask your grown-up for help with anything you are not sure about.',
  },
  {
    id: 'messages',
    title: 'Family messages keep your grown-up included',
    body: 'Read messages and acknowledge the ones that need an answer. Your grown-up can always see the same conversation.',
  },
  {
    id: 'classes',
    title: 'Class boards hold updates and practice links',
    body: 'Open a class to read updates or post a message. Your grown-up is included automatically when you post.',
  },
];

export function welcomeTourStepsForRole(role: WelcomeTourRole): readonly WelcomeTourStep[] {
  if (role === 'dancer') return DANCER_STEPS;
  if (role === 'family') return FAMILY_STEPS;
  if (role === 'foh') return FRONT_DESK_STEPS;
  if (role === 'director') {
    return [...STAFF_STEPS, DIRECTOR_EXTRA_STEP];
  }
  return STAFF_STEPS;
}
